import { useEffect, useState, useRef, useContext } from "react";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import HeaderWithBackButton from "../common/HeaderWIthBackButton";
import DocumentTable from "./DocumentTable";
import DocumentService from "../../services/DocumentService";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import { useFormik } from "formik";
import { contextStore } from "../../context/CustomStateProvider";
import fileUpload from "../../services/fileUpload";
import axios from "axios";
import DocumentUploadValidationSchema from "../../validation/DocumentUploadValidation";

export type DocumentItem = {
  pdfId: number;
  pdfName: string;
  category: string;
  description: string;
  uploadedDate: string;
  errormessage: string | null;
  bloberrormessage: string | null;
  userId: number;
};

const DocumentLayout: React.FC = () => {
  const context = useContext(contextStore);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loadingSpinnerLayout, setLoadingSpinnerLayout] =
    useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [reloadDocs, setReloadDocs] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const resource = context.state.strings;
  const [progressForDocument, setProgressForDocument] = useState<number>(0);
  const [documentFileName, setDocumentFileName] = useState<string>("");
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [uploadVisible, setUploadVisible] = useState(false);
  const [embeddingInProgress, setEmbeddingInProgress] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 🔹 Fetch documents
  const fetchDocuments = async () => {
    try {
      const res = await DocumentService.GetAllDocuments();
      if (res && Array.isArray((res as any).data)) {
        setDocuments((res as any).data);
      } else if (Array.isArray(res)) {
        setDocuments(res);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setTimeout(() => setLoadingSpinnerLayout(false), 400);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [reloadDocs]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredDocs = documents.filter((doc) =>
    (doc?.pdfName ?? "")
      .toLowerCase()
      .includes((searchQuery ?? "").toLowerCase())
  );

  // 🔹 File select and upload
  const onDocumentFileSelect = (event: any) => {
    const file: File = event.files[0];
    if (!file) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Something went wrong.",
        life: 3000,
      });
      return;
    }

    if (!file.type.startsWith("application/pdf")) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a PDF file only.",
        life: 3000,
      });
      return;
    }

    setSelectedFile(file);
    setDocumentFileName(file.name);
    formik.setFieldValue("documentUpload", file.name);
    // UploadDocumentFile({
    //   File: file,
    //   DocumentName: formik.values.documentname,
    //   Description: formik.values.description,
    // });
  };

  const handleSubmit = async (values: any) => {
    if (!selectedFile) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a document before submitting.",
        life: 3000,
      });
      return;
    }

    UploadDocumentFile({
      File: selectedFile,
      DocumentName: values.documentname,
      Category: values.category,
      Description: values.description,
    });
  };

  const initialValues = {
    documentname: "",
    category: "",
    description: "",
    documentUpload: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: DocumentUploadValidationSchema,
    // onSubmit: () => {},
    onSubmit: handleSubmit,
  });

  const simulateEmbeddingProgress = async (callback: Function) => {
    setEmbeddingInProgress(true);
    setProgressForDocument(60); // Start embedding from 60%

    const interval = setInterval(() => {
      setProgressForDocument((prev) => {
        if (prev >= 95) {
          clearInterval(interval);
          return prev;
        }
        return prev + 2; // Smooth incremental increase
      });
    }, 300);

    try {
      await callback(); // Actually call process_pdf
    } finally {
      clearInterval(interval);
      setProgressForDocument(100);
      setEmbeddingInProgress(false);
    }
  };

  const UploadDocumentFile = (data: any) => {
    fileUpload
      .PDFUpload(data, (progress: number) => setProgressForDocument(progress))
      .then(async (res: any) => {
        if (res.status === 200) {
          const responseData = res.data;
          const uploadedPdfId =
            responseData?.p_pdfid || responseData?.pdfId || responseData?.id;
          const presignedURL = responseData?.presigned_url;

          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail:
              "The PDF has been successfully uploaded to the Blob storage.",
            life: 3000,
          });

          if (uploadedPdfId) {
            await simulateEmbeddingProgress(async () => {
              await callProcessPdfEmbedding(
                data.File,
                uploadedPdfId,
                presignedURL
              );
            });
            // setUploadVisible(false);
            hideUploadDialog();
          }

          setDocumentFileName("");
          setReloadDocs((prev) => !prev);
        }

        setTimeout(() => setProgressForDocument(0), 2000);
      })
      .catch(async (error) => {
        console.error("Blob upload failed:", error);

        toast.current?.show({
          severity: "error",
          summary: "Upload Failed",
          detail: "Failed to upload document to Blob storage.",
          life: 4000,
        });

        await logBlobUploadError(error?.response?.data?.pdfid || 0, error);
      });
  };

  const callProcessPdfEmbedding = async (
    file: File,
    uploadPdfId: number,
    presignedURL: string
  ) => {
    try {
      const formData = new FormData();
      formData.append("pdf_file", file);
      formData.append("upload_pdf_id", uploadPdfId.toString());
      formData.append("presigned_url", presignedURL);

      const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL ||
        // "https://cc4jaxi5vi.execute-api.ap-south-1.amazonaws.com/Prod";
        "https://chataiapi-dev.vedbhoomi.co.in";
      const response = await axios.post(
        `${API_BASE_URL}/process_pdf`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200) {
        toast.current?.show({
          severity: "success",
          summary: "Embedding Started",
          detail: `Embedding initiated for ${file.name}`,
          life: 3000,
        });
      }
    } catch (error: any) {
      console.error("Error during embedding:", error);
      toast.current?.show({
        severity: "error",
        summary: "Embedding Failed",
        detail: "Unable to process embedding for this document.",
        life: 4000,
      });
      // log embedding api call below
      await logEmbeddingError(uploadPdfId, error);
    }
  };

  const logEmbeddingError = async (pdfId: number, errorObj: any) => {
    const errorMessage =
      errorObj?.response?.data?.message ||
      errorObj?.message ||
      "Unable to process embedding for this document.";

    const logData = {
      pdfid: pdfId,
      isuploaded: false,
      errormessage: errorMessage,
    };

    try {
      await fileUpload.LogEmbeddingError(logData);

      console.log("Embedding error logged successfully.");
    } catch (error) {
      console.error("Failed to log embedding error:", error);
    }
  };

  const logBlobUploadError = async (pdfId: number, errorObj: any) => {
    const errorMessage =
      errorObj?.response?.data?.message ||
      errorObj?.message ||
      "Blob upload failed.";

    const logData = {
      pdfid: pdfId,
      isuploaded: false,
      errormessage: null,
      bloberrormessage: errorMessage,
    };

    try {
      await fileUpload.LogEmbeddingError(logData);
      console.log("Blob error logged successfully.");
    } catch (error) {
      console.error("Failed to log blob upload error:", error);
    }
  };

  // const hideUploadDialog = () => setUploadVisible(false);
  const hideUploadDialog = () => {
    setUploadVisible(false);
    formik.resetForm();
    setSelectedFile(null);
    setDocumentFileName("");
    setProgressForDocument(0);
  };
  const onUploadDocumentClick = () => setUploadVisible(true);

  const onBackButtonClick = () => {
    toast.current?.show({
      severity: "info",
      summary: "Back",
      detail: "Returning to previous page.",
      life: 2000,
    });
  };

  const cardControls = [
    {
      ctrlType: "button",
      name: "btnUpload",
      title: resource.uploadDocuments || "Upload Knowledge Base",
      type: "button",
      disabled: false,
      className: "green-btn mr-2",
    },
  ];

  const onButtonClick = (e: string) => {
    if (e === "btnUpload") {
      onUploadDocumentClick();
    }
  };

  return (
    <div className="ml-2">
      <Toast ref={toast} />
      <input
        type="file"
        accept="application/pdf"
        ref={hiddenFileInput}
        style={{ display: "none" }}
      />

      {loadingSpinnerLayout ? (
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "70vh" }}
        >
          <ProgressSpinner
            style={{ width: "70px", height: "70px" }}
            strokeWidth="2"
            fill="var(--surface-ground)"
            animationDuration=".9s"
          />
        </div>
      ) : (
        <>
          <HeaderWithBackButton
            title={resource.documentTitle}
            controls={cardControls}
            onButtonClicked={onButtonClick}
            onBackButtonClick={onBackButtonClick}
            handleSearch={handleSearch}
            searchQuery={searchQuery}
          />

          {/* ✅ Upload Dialog */}
          <Dialog
            className="popup popup-card-layout"
            visible={uploadVisible}
            draggable={false}
            // closable={!embeddingInProgress}
            closable={progressForDocument === 0}
            style={{ width: "60vw" }}
            onHide={hideUploadDialog}
            header={
              <div className="flex justify-content-between align-items-center w-full">
                <span>{resource.uploadDocumentHeader}</span>
                <i
                  className="pi pi-times cursor-pointer text-gray-600 hover:text-gray-900"
                  onClick={hideUploadDialog}
                  style={{
                    fontSize: "1.2rem",
                    marginRight: "0.5rem",
                  }}
                ></i>
              </div>
            }
          >
            <form onSubmit={formik.handleSubmit}>
              <div className="card-wrapper">
                <div className="flex mb-2">
                  <div className="col-4 relative">
                    <label
                      htmlFor="documentname"
                      className="block text-900 font-medium mb-2"
                    >
                      {resource.documentname}{" "}
                      <span className="text-red">*</span>
                    </label>
                    <InputText
                      id="documentname"
                      type="text"
                      placeholder="Enter name"
                      className="w-full mb-2"
                      value={formik.values.documentname}
                      onChange={formik.handleChange}
                      tabIndex={1}
                      ref={inputRef}
                    />
                    {formik.errors.documentname &&
                      formik.touched.documentname && (
                        <div className="text-red-500 text-xs">
                          {formik.errors.documentname}
                        </div>
                      )}
                  </div>
                  <div className="col-4 relative">
                    <label
                      htmlFor="category"
                      className="block text-900 font-medium mb-2"
                    >
                      {resource.category}{" "}
                      {/* <span className="text-red">*</span> */}
                    </label>
                    <InputText
                      id="category"
                      type="text"
                      placeholder="Enter category"
                      className="w-full mb-2"
                      value={formik.values.category}
                      onChange={formik.handleChange}
                      tabIndex={1}
                      ref={inputRef}
                    />
                    {/* {formik.errors.category &&
                      formik.touched.category && (
                        <div className="text-red-500 text-xs">
                          {formik.errors.category}
                        </div>
                      )} */}
                  </div>
                  <div className="col-4 relative">
                    <label
                      htmlFor="description"
                      className="block text-900 font-medium mb-2"
                    >
                      {resource.docDescription}
                    </label>
                    <InputTextarea
                      id="description"
                      placeholder="Enter description"
                      className="w-full mb-2"
                      rows={3}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      tabIndex={2}
                    />
                    {/* {formik.errors.docDescription && formik.touched.docDescription && (
                      <div className="text-red-500 text-xs">
                        {formik.errors.docDescription}
                      </div>
                    )} */}
                  </div>
                </div>
                <div className="flex mb-2">
                  <div className="col-4 relative">
                    <label
                      htmlFor="documentUpload"
                      className="block text-900 font-medium mb-2"
                    >
                      {resource.documentUpload}{" "}
                      <span className="text-red">*</span>
                    </label>
                    <div className="flex">
                      <InputText
                        id="documentUpload"
                        type="text"
                        placeholder="Select document"
                        className="fileinput ml-1"
                        value={documentFileName}
                        onChange={formik.handleChange}
                        tabIndex={3}
                      />
                      <FileUpload
                        mode="basic"
                        accept="application/pdf"
                        auto
                        customUpload
                        chooseLabel="Select"
                        className="flex justify-content-center fileselectbtn"
                        style={{ height: "40px" }}
                        // onSelect={onDocumentFileSelect}
                        onSelect={(event) => {
                          onDocumentFileSelect(event);
                          formik.setFieldValue(
                            "documentUpload",
                            event.files[0]?.name || ""
                          );
                        }}
                      />
                      {formik.errors.documentUpload &&
                        formik.touched.documentUpload && (
                          <div className="text-red-500 text-xs">
                            {formik.errors.documentUpload}
                          </div>
                        )}
                      {progressForDocument > 0 && progressForDocument < 100 && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginTop: "10px",
                            marginLeft: "5px",
                          }}
                        >
                          <ProgressSpinner
                            style={{ width: "30px", height: "30px" }}
                            strokeWidth="4"
                            fill="transparent"
                            animationDuration=".5s"
                          />
                          <span style={{ whiteSpace: "nowrap" }}>
                            {/* {progressForDocument}% Uploading... */}
                            {progressForDocument < 60
                              ? `${progressForDocument}% Uploading...`
                              : `${progressForDocument}% Processing file...`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="cancel-submit-btn mt-8">
                <Button
                  label="Submit"
                  className="submit-btn ml-2"
                  type="submit"
                  tabIndex={4}
                />
              </div>
            </form>
          </Dialog>

          <div className="transparant-table">
            <div className="row flex">
              <div className="col-12">
                <DocumentTable
                  documents={filteredDocs}
                  loading={loadingSpinnerLayout}
                  onDelete={() => setReloadDocs((prev) => !prev)}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentLayout;
